import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";

const BalanceContext = createContext(null);

export const useBalance = () => {
  const context = useContext(BalanceContext);
  if (!context) throw new Error("useBalance must be used within BalanceProvider");
  return context;
};

export const BalanceProvider = ({ children }) => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  const fetchBalance = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/account/balance`, getAuthConfig());
      setBalance(res.data);
    } catch (err) {
      if (err.response?.status !== 401) {
        console.error("Failed to fetch balance:", err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalance();
    const interval = setInterval(fetchBalance, 30000);
    return () => clearInterval(interval);
  }, [fetchBalance]);

  const isLowBalance = () => {
    if (!balance) return false;
    return balance.balance <= (balance.low_balance_threshold || 5);
  };

  const hasBalance = () => {
    if (!balance) return false;
    return balance.balance > 0;
  };

  return (
    <BalanceContext.Provider value={{
      balance,
      loading,
      refetch: fetchBalance,
      isLowBalance,
      hasBalance,
      currentBalance: balance?.balance ?? 0,
      overageRates: balance?.overage_rates ?? {},
      topupOptions: balance?.topup_options ?? [10, 25, 50, 100, 250],
    }}>
      {children}
    </BalanceContext.Provider>
  );
};

export default BalanceContext;
